// Used in resources.component.html and student-resources-container.component.html

// Angular
import { animate, style, transition, trigger } from "@angular/animations";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";

// RXJS/Store
import { Observable, Subject } from "rxjs";

// Services/Types/Helpers
import { LocalStorage } from "#core/services";
import { RouteNamesHelper } from "#helpers/route-names.helper";
import { EditionId, SubjectId } from "@carnegie-learning/common-types";
import {
    Asset,
    AssetAssociation,
    assetActivityTypeIds,
    assetTypeIds,
    eBookActivity,
    resourceTypeIds,
} from "@carnegie-learning/cure-types";
import { ASSET_FILES_SERVICE } from "@carnegie-learning/data-access-angular-cure";
import { GetBookshelfUrlHelper } from "app/core/helpers/get-bookshelf-url.helper";
import {
    AssetTypeHelper,
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
    ResourceOptions,
    PPUser as User,
} from "cli-types";
import { AssetFilesService } from "@carnegie-learning/data-access-cure";

@Component({
    selector: "tiles-resources",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: "./tiles-resources.component.html",
    styleUrls: ["./tiles-resources.component.scss", "../../styles/shared-styles.scss"],
    animations: [
        trigger("fadeInOut", [
            transition(":leave", [
                // :leave is alias to '* => void'
                animate(500, style({ opacity: 0 })),
            ]),
        ]),
        trigger("collapse", [
            transition(":enter", [
                // :enter is alias to 'void => *'
                style({ height: 0, overflow: "hidden" }),
                animate(300, style({ height: "100%" })),
            ]),
            transition(":leave", [
                // :leave is alias to '* => void'
                style({ overflow: "hidden" }),
                animate(250, style({ height: 0 })),
            ]),
        ]),
    ],
    standalone: false,
})
export class TilesResourcesComponent implements OnDestroy, OnInit, OnChanges {
    private ngUnsubscribe = new Subject();
    private assetFileService = inject<AssetFilesService>(ASSET_FILES_SERVICE);
    isMakeQuest: boolean;
    isCogTutor: boolean;
    public innerWidth: any;
    tileID: number;
    private S3_IMAGE_PATH = "images/";
    embedUrl: any;
    activity: CompositeTaskAssignment;
    loading: boolean;
    loadingMoreAssignments: boolean;
    count: number;
    pacingData: {
        tile: Assignment;
        pacingData: { actual?: string; pacingEstimate?: string };
    }[];
    filteredTileData: Assignment[] | Asset[] | resourceTypeIds[]; // receive assignments or resource data from parent components
    s3Files: Map<string, Observable<string>> = new Map<string, Observable<string>>();
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;

    @Input() tileData: Assignment[] | Asset[] | resourceTypeIds[]; // receive assignments or resource data from parent components
    @Input() selectedTile: Assignment | Asset | null; // receive current selected tile from parent components
    @Input() course: Course;
    // Resources Inputs
    @Input() public studentMetrics: any;
    @Input() public courseID: number;
    @Input() public subjectId: SubjectId;
    @Input() public editionId: EditionId;
    @Input() public subject: AssetAssociation;
    @Input() public profile: User;
    @Input() assignmentsLoaded: boolean;
    @Input() noMoreResults: boolean;
    @Input() isTeacher: boolean;
    @Input() isStudent: boolean;
    @Input() listView: boolean;
    @Input() resourceTiles: boolean;
    @Input() resourceCategories = false;
    @Input() resourceOptions: ResourceOptions[];
    @Input() allEditions: AssetAssociation[];

    @Output() loadMoreAssignments = new EventEmitter<any>();
    @Output()
    public setActiveAssignmentsEvent = new EventEmitter<Notification>();
    @Output() public setActiveResourceEvent = new EventEmitter<Notification>();
    @Output() public getFullAssignment = new EventEmitter<number>();
    @Output() public setSelectedAssignment = new EventEmitter<Assignment>();
    @Output() public setSelectedResource = new EventEmitter<Asset>();
    @Output() public setMarkedAsRead = new EventEmitter<Assignment | CompositeTaskAssignment>();
    @Output()
    public setMarkedAsReadForFeedback = new EventEmitter<Assignment>();
    @Output() setCategory = new EventEmitter<string>();

    @ViewChild(MatPaginator) paginator: MatPaginator;

    constructor(
        private getBookshelfUrl: GetBookshelfUrlHelper,
        public dialog: MatDialog,
        private routeNamesHelper: RouteNamesHelper,
        private localStorage: LocalStorage,
        private assetTypeHelper: AssetTypeHelper
    ) {}

    ngOnInit() {
        this.getOnlySharedWithStudents(<Asset[]>this.tileData);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["tileData"] || changes["resourceOptions"]) {
            this.getOnlySharedWithStudents(<Asset[]>this.tileData);
        }
    }

    // used to get the ID of the tiles
    getID(tile: Asset) {
        return (<eBookActivity>tile).bookId;
    }

    // Opens Resource
    openResource(tile: Asset) {
        if (tile) {
            if (this.resourceTiles) {
                this.setSelectedResource.emit(<Asset>tile);
            }
        }
    }

    getUrlForTile(tile: Asset) {
        if (tile) {
            if (this.resourceTiles) {
                return this.routeNamesHelper.getRoutePath(
                    "resource",
                    this.courseID,
                    null,
                    null,
                    tile.assetId,
                    null,
                    null,
                    this.editionId || this.course?.editionId
                );
            }

            return this.routeNamesHelper.getRoutePath(
                "resource",
                this.courseID,
                null,
                null,
                (<eBookActivity>tile).bookId || (<eBookActivity>tile).externalId,
                null,
                null,
                null
            );
        }
    }

    getUrlForNewTab(asset: eBookActivity) {
        if (!this.resourceTiles) {
            return null;
        }

        const resourceOption: ResourceOptions | undefined = this.resourceOptions?.find(
            (opt) => opt.assetId === asset.assetId
        );
        const url = this.getBookshelfUrl.book(
            asset.bookId || asset.externalId,
            this.courseID,
            this.profile,
            resourceOption?.practice ? 1 : 0
            // spine.inQA
        );
        return url;
    }

    // resource image
    getTileBackgroundImageStyle(tile: Assignment | CompositeTaskAssignment | Asset) {
        if (tile && this.resourceTiles) {
            return "linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0))";
        }
    }

    getTileBackgroundImageUrl(tile: Assignment | CompositeTaskAssignment | Asset) {
        if (tile && this.resourceTiles) {
            let largeSuffix = "-lg.";
            if ((<eBookActivity>tile).bookType === "crv") {
                largeSuffix = "@2x.";
            }
            return this.getImageUrl(
                this.S3_IMAGE_PATH +
                    (<eBookActivity>tile).bookImage +
                    largeSuffix +
                    (<eBookActivity>tile).bookImageExt
            );
        }
    }

    getImageUrl(fileName: string) {
        if (!this.s3Files.has(fileName)) {
            this.s3Files.set(fileName, this.assetFileService.getFileUrl(fileName));
        }
        return this.s3Files.get(fileName);
    }

    // background color for tiles (primarily used for assignments without images)
    getTileBackgroundColor() {
        return "#d3d3d3";
    }

    getSubjectId(asset: Asset) {
        return asset.subjectId;
    }

    getCategoryTileName(category: resourceTypeIds): string {
        return this.assetTypeHelper.getCapTypeName(category);
    }
    getCategoryIcon(category: resourceTypeIds): string {
        return this.assetTypeHelper.convertShortAssignableTypeToIconPath(category, true);
    }
    setCategoryFn(category: resourceTypeIds) {
        this.setCategory.emit(category);
    }
    getBannerClass(category: resourceTypeIds): string {
        let className = "";
        if (category) {
            className = category + "-banner";
        }
        return className;
    }
    getOnlySharedWithStudents(tileData: Assignment[] | Asset[] | resourceTypeIds[]) {
        if (tileData && this.resourceOptions && !this.isTeacher) {
            const filteredTileData = (<Asset[]>tileData).filter((tile) => {
                let share = true;
                this.resourceOptions.forEach((resourceOption) => {
                    if (resourceOption.assetId === (<Asset>tile).assetId) {
                        share = !!resourceOption.shareWithStudents;
                    }
                });

                return share;
            });
            this.filteredTileData = filteredTileData;
        } else {
            this.filteredTileData = tileData;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
